/**
 * EntitlementsProvider — React surface for the shared entitlements state
 * machine. Both clients render UI off the same context. Caller injects a
 * minimal billing API surface (just `getUserBalance`) so this module stays
 * platform-agnostic and only depends on @wg/types/billing.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { BalanceResponse } from '@wg/types/billing';
import type { Logger } from '@wg/api-client';
import { calculateEffectiveBalance, type TimeAllowance } from './types';

const REFRESH_INTERVAL_MS = 60_000;
const LOW_BALANCE_THRESHOLD = 120;
const WARNING_BALANCE_THRESHOLD = 60;

const AGGRESSIVE_POLL_INTERVALS = [2000, 3000, 5000, 8000, 15000, 30000, 60000];
const MAX_AGGRESSIVE_POLLS = 12;

export interface EntitlementsBillingApi {
  getUserBalance(breakdown?: boolean): Promise<BalanceResponse>;
}

export interface EntitlementsState {
  billingEnabled: boolean;
  balance: number | null;
  balanceLoading: boolean;
  balanceError: string | null;
  allowance: TimeAllowance | null;
  effectiveBalance: number | null;
  hasUnmeteredAccess: boolean;
  /** Credits with no expiry. */
  boostCredits: number | null;
  /** Credits that expire. */
  limitedCredits: number | null;
  /** Earliest expiry across all limited credits. */
  earliestExpiry: Date | null;
  isLowBalance: boolean;
  isWarningBalance: boolean;
  isCriticalBalance: boolean;
  isDepleted: boolean;
  isAggressivePolling: boolean;
  refreshBalance: () => Promise<void>;
  refreshEntitlements: () => Promise<void>;
  refreshAll: () => Promise<void>;
  /** Kicks off exponential-backoff polling after a redeem/purchase. */
  startAggressivePolling: () => void;
}

const EntitlementsContext = createContext<EntitlementsState | undefined>(
  undefined,
);

export interface EntitlementsProviderProps {
  billingApi: EntitlementsBillingApi;
  logger: Logger;
  /** From the shared AuthContext — gates fetches. */
  isAuthenticated: boolean;
  /**
   * Document-visibility listener wiring. Pass `() => () => {}` to no-op on
   * RN; web passes a wrapper around `visibilitychange`.
   */
  onForegroundReturn?: (cb: () => void) => () => void;
  children: React.ReactNode;
}

export const EntitlementsProvider: React.FC<EntitlementsProviderProps> = ({
  billingApi,
  logger,
  isAuthenticated,
  onForegroundReturn,
  children,
}) => {
  const [billingEnabled, setBillingEnabled] = useState(true);
  const [balance, setBalance] = useState<number | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [allowance, setAllowance] = useState<TimeAllowance | null>(null);
  const [hasUnmeteredAccess, setHasUnmeteredAccess] = useState(false);
  const [serverEffectiveBalance, setServerEffectiveBalance] = useState<
    number | null
  >(null);
  const [boostCredits, setBoostCredits] = useState<number | null>(null);
  const [limitedCredits, setLimitedCredits] = useState<number | null>(null);
  const [earliestExpiry, setEarliestExpiry] = useState<Date | null>(null);
  const [isAggressivePolling, setIsAggressivePolling] = useState(false);

  const cancelledRef = useRef(false);
  const aggressivePollingTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const aggressivePollCountRef = useRef(0);
  const balanceBeforePollingRef = useRef<number | null>(null);
  const balanceRef = useRef<number | null>(null);
  balanceRef.current = balance;

  const effectiveBalance = useMemo(() => {
    if (
      serverEffectiveBalance !== null &&
      serverEffectiveBalance !== undefined
    ) {
      return serverEffectiveBalance;
    }
    return calculateEffectiveBalance(
      balance,
      allowance ?? undefined,
      hasUnmeteredAccess,
    );
  }, [serverEffectiveBalance, balance, allowance, hasUnmeteredAccess]);

  const isLowBalance =
    effectiveBalance !== null &&
    effectiveBalance < LOW_BALANCE_THRESHOLD &&
    effectiveBalance > 0;
  const isWarningBalance =
    effectiveBalance !== null &&
    effectiveBalance < WARNING_BALANCE_THRESHOLD &&
    effectiveBalance > 0;
  const isCriticalBalance = effectiveBalance !== null && effectiveBalance <= 0;
  const isDepleted = effectiveBalance !== null && effectiveBalance <= 0;

  const applyBalance = useCallback((data: BalanceResponse) => {
    if (cancelledRef.current) return;
    if (data.enabled === false) {
      setBillingEnabled(false);
      setBalance(null);
      setAllowance(null);
      setHasUnmeteredAccess(false);
      setServerEffectiveBalance(null);
      setBoostCredits(null);
      setLimitedCredits(null);
      setEarliestExpiry(null);
      return;
    }
    setBillingEnabled(true);
    setBalance(data.balance ?? null);
    setHasUnmeteredAccess(!!data.has_unmetered_access);
    setServerEffectiveBalance(data.effective_balance ?? data.balance ?? null);

    if (data.quota) {
      setAllowance({
        amount: data.quota.quota_limit,
        period: 86_400,
        periodName: 'daily',
        used: data.quota.consumed,
        remaining: data.quota.available,
        resetsAt: data.quota.next_reset
          ? new Date(data.quota.next_reset)
          : new Date(),
        stackedCount: data.quota.stacked_count,
      });
    } else if (data.allowance) {
      setAllowance({
        amount: data.allowance.amount,
        period: data.allowance.period_seconds,
        periodName: data.allowance.period,
        used: data.allowance.used,
        remaining: data.allowance.remaining,
        resetsAt: new Date(data.allowance.resets_at),
        stackedCount: 1,
      });
    } else {
      setAllowance(null);
    }

    const breakdown = data.breakdown;
    if (Array.isArray(breakdown) && breakdown.length > 0) {
      let boostTotal = 0;
      let limitedTotal = 0;
      let earliestStr: string | null = null;
      for (const item of breakdown) {
        if (item?.earliest_expiry == null) {
          boostTotal += Number(item?.amount) || 0;
        } else {
          limitedTotal += Number(item?.amount) || 0;
          if (!earliestStr || item.earliest_expiry < earliestStr) {
            earliestStr = item.earliest_expiry;
          }
        }
      }
      setBoostCredits(boostTotal);
      setLimitedCredits(limitedTotal);
      setEarliestExpiry(earliestStr ? new Date(earliestStr) : null);
    } else {
      setBoostCredits(null);
      setLimitedCredits(null);
      setEarliestExpiry(null);
    }
  }, []);

  const refreshBalance = useCallback(async () => {
    if (!isAuthenticated) {
      setBalance(null);
      setAllowance(null);
      setServerEffectiveBalance(null);
      setBoostCredits(null);
      setLimitedCredits(null);
      setEarliestExpiry(null);
      return;
    }
    try {
      setBalanceLoading(true);
      setBalanceError(null);
      const data = await billingApi.getUserBalance(true);
      applyBalance(data);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number } };
      const status = e?.response?.status;
      if (status === 401 || status === 404) {
        if (!cancelledRef.current) setBillingEnabled(false);
      } else {
        logger.warn('[entitlements] balance refresh failed', err);
        if (!cancelledRef.current) setBalanceError('Could not load balance');
      }
    } finally {
      if (!cancelledRef.current) setBalanceLoading(false);
    }
  }, [isAuthenticated, applyBalance, billingApi, logger]);

  const startAggressivePolling = useCallback(() => {
    logger.info('[entitlements] Starting aggressive post-purchase polling');
    if (aggressivePollingTimeoutRef.current) {
      clearTimeout(aggressivePollingTimeoutRef.current);
    }
    aggressivePollCountRef.current = 0;
    balanceBeforePollingRef.current = balanceRef.current;
    setIsAggressivePolling(true);
    refreshBalance();

    const scheduleNextPoll = () => {
      const pollCount = aggressivePollCountRef.current;
      if (pollCount >= MAX_AGGRESSIVE_POLLS) {
        setIsAggressivePolling(false);
        return;
      }
      const intervalIndex = Math.min(
        pollCount,
        AGGRESSIVE_POLL_INTERVALS.length - 1,
      );
      const interval = AGGRESSIVE_POLL_INTERVALS[intervalIndex];
      aggressivePollingTimeoutRef.current = setTimeout(async () => {
        aggressivePollCountRef.current += 1;
        await refreshBalance();
        const after = balanceRef.current;
        const before = balanceBeforePollingRef.current;
        const grew = after !== null && before !== null && after > before;
        if (grew) {
          setIsAggressivePolling(false);
          return;
        }
        scheduleNextPoll();
      }, interval);
    };
    scheduleNextPoll();
  }, [refreshBalance, logger]);

  useEffect(() => {
    cancelledRef.current = false;
    refreshBalance();
    const id = setInterval(refreshBalance, REFRESH_INTERVAL_MS);
    const unsubFg = onForegroundReturn?.(refreshBalance);
    return () => {
      cancelledRef.current = true;
      clearInterval(id);
      unsubFg?.();
      if (aggressivePollingTimeoutRef.current) {
        clearTimeout(aggressivePollingTimeoutRef.current);
      }
    };
  }, [refreshBalance, onForegroundReturn]);

  const refreshEntitlements = refreshBalance;
  const refreshAll = refreshBalance;

  const value: EntitlementsState = {
    billingEnabled,
    balance,
    balanceLoading,
    balanceError,
    allowance,
    effectiveBalance,
    hasUnmeteredAccess,
    boostCredits,
    limitedCredits,
    earliestExpiry,
    isLowBalance,
    isWarningBalance,
    isCriticalBalance,
    isDepleted,
    isAggressivePolling,
    refreshBalance,
    refreshEntitlements,
    refreshAll,
    startAggressivePolling,
  };

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
};

export function useEntitlements(): EntitlementsState {
  const ctx = useContext(EntitlementsContext);
  if (!ctx) {
    throw new Error(
      'useEntitlements must be used within an EntitlementsProvider',
    );
  }
  return ctx;
}
